import { colors } from './colors';
import { fontSizes } from './fontSizes';
import { fontWeights } from './fontWeights';
import { space } from './space';
import type { Components } from './types/components';
import { generateSpacings, px2rem } from './utils';

export const components: Components = {
  mobileMenu: {
    backdropFilter: 'blur(1.25rem)',
  },
  mobileNav: {
    innerListItem: {
      fontSize: '1.125rem',
    },
    width: '25rem',
  },
  breadcrumb: {
    dot: {
      width: '.25rem',
      height: '.25rem',
    },
    expander: {
      borderRadius: '.125rem',
      gap: '.125rem',
      height: '.625rem',
      width: '1.5rem',
      marginRight: '.375rem',
    },
  },
  button: {
    borderWidth: 2,
    colorVariant: {
      primary: {
        filled: {
          bg: colors.primary,
          color: colors.default,
          disabledColor: colors.default,
          hoverColor: colors.black,
          hoverBg: colors.white,
        },
        outlined: {
          color: colors.primaryDark,
          hoverBg: colors.primaryDark,
          hoverBorderColor: colors.primaryDark,
          hoverColor: colors.white,
        },
        link: {
          color: colors.primaryDark,
          hoverColor: colors.primaryDark,
          disabledColor: colors.primary,
        },
      },
      secondary: {
        filled: {
          bg: colors.primaryDark,
          color: colors.white,
          disabledColor: colors.white,
          hoverColor: colors.primary,
        },
        outlined: {
          color: colors.primaryDark,
          hoverBg: colors.primaryDark,
          hoverBorderColor: colors.primaryDark,
          hoverColor: colors.white,
        },
        link: {
          color: colors.primaryDark,
          hoverColor: colors.primary,
          disabledColor: colors.primary,
        },
      },
      black: {
        filled: {
          bg: colors.black,
          hoverBg: colors.transparent,
          color: colors.white,
          disabledColor: colors.black,
          hoverColor: colors.black,
          hoverBorderColor: colors.black,
        },
        outlined: {
          color: colors.black,
          hoverBg: colors.primary,
          hoverBorderColor: colors.primary,
          hoverColor: colors.black,
        },
        link: {
          color: colors.black,
          hoverColor: colors.black,
          disabledColor: colors.primaryDark,
        },
      },
      blue: {
        filled: {
          bg: colors.navyBlueSub1,
          color: colors.white,
          disabledColor: colors.black,
          hoverColor: colors.primary,
        },
        outlined: {
          color: colors.navyBlueSub1,
          hoverBg: colors.primaryDark,
          hoverBorderColor: colors.primaryDark,
          hoverColor: colors.white,
        },
        link: {
          color: colors.navyBlueSub1,
          hoverColor: colors.primaryDark,
          disabledColor: colors.primaryBlue,
        },
      },
      white: {
        filled: {
          bg: colors.white,
          color: colors.black,
          disabledColor: colors.white,
          hoverColor: colors.primary,
          hoverBg: colors.white,
          hoverBorderColor: colors.white,
        },
        outlined: {
          color: colors.white,
          hoverBg: colors.primary,
          hoverBorderColor: colors.primary,
          hoverColor: colors.white,
        },
        link: {
          color: colors.white,
          hoverColor: colors.primary,
          disabledColor: colors.secondary,
        },
      },
      gray: {
        filled: {
          bg: colors.mediumGrey,
          color: colors.default,
          disabledColor: colors.default,
          hoverColor: colors.default,
          hoverBg: colors.primary,
        },
        outlined: {
          color: colors.secondary,
          hoverBg: colors.primary,
          hoverBorderColor: colors.primary,
          hoverColor: colors.secondary,
        },
        link: {
          color: colors.default,
          hoverColor: colors.primary,
          disabledColor: colors.primaryBlue,
        },
      },
    },
  },
  carousel: {
    normal: space.s200,
    condensed: space.s100,
  },
  checkbox: {
    borderWidth: 1,
    icon: {
      height: 18,
      width: 20,
    },
    size: {
      height: 20,
      width: 20,
    },
    markColor: colors.primary,
  },
  chip: {
    color: colors.default,
    borderWidth: 1,
  },
  switchComponent: {
    borderRadius: '100px',
    size: {
      height: '2.1875rem',
      width: '5rem',
    },
    thickSize: {
      width: '1.0625rem',
      height: '0.4375rem',
    },
    sliderSize: {
      width: '2.25rem',
      height: '1.6875rem',
    },
  },
  countrySelector: {
    closeIcon: {
      variant: {
        desktop: {
          right: '1rem',
          top: '1rem',
        },
        mobile: {
          right: '0.625rem',
          top: '0.625rem',
        },
      },
    },
    contentWrapper: {
      gap: '6rem',
      padding: '1.5rem',
    },
    mobileContentWrapper: {
      gap: '1rem',
      paddingBottom: '1rem',
      paddingTop: '2.125rem',
    },
    tile: {
      fontSize: px2rem(12),
      variant: {
        desktop: {
          width: '6rem',
        },
        mobile: {
          width: '6.813rem',
        },
      },
    },
    wrapper: {
      borderWidth: 1,
    },
  },
  heading: {
    blockWidth: '0',
    blockBorder: '0',
    blockMargin: '0',
    highlightBlock: 'inline-block',
  },
  heroBanner: {
    title: {
      padding: `${generateSpacings(10)} 5%`,
    },
  },
  cmsCard: {
    content: {
      desktopMaxWidth: {
        Md: '880px',
        MediaBannerOrMediaAndBottomText: '560px',
        MediaAndSideTextOrSectionBanner: '480px',
        Default: '',
      },
    },
    baseCard: {
      mobile: {
        padding: generateSpacings(3, 2, 4, 2),
        paddingHasWrapperMinHeight: generateSpacings(2, 2),
      },
    },
  },
  header: {
    // Change to a dynamic value if needed
    defaultBackground: 'yellow',
    breakpoint: '72.5rem',
    innerWrapper: {
      maxWidth: '90rem',
      paddingInline: `${space.s250}`,
      paddingInlineMobile: '1rem 0',
    },
    wrapper: {
      height: {
        mobile: '5.5rem',
        regular: '4rem',
        shrunk: '3rem',
      },
    },
    megamenu: {
      fontSize: `${fontSizes.p0}`,
    },
    theme: {
      white: {
        default: {
          icon: {
            color: colors.primaryBlue,
          },
          menu: {
            color: colors.almostBlack,
          },
          text: {
            color: colors.primaryBlue,
          },
        },
        hover: {
          icon: {
            color: colors.primary,
          },
          text: {
            color: colors.primaryBlue,
          },
          headerInfoText: {
            color: colors.primaryBlue,
          },
        },
        active: {
          menu: {
            color: colors.primary,
          },
          text: {
            color: colors.primaryBlue,
          },
        },
        accessibilityIcon: {
          path1: {
            color: colors.primaryBlue,
          },
          path2: {
            color: colors.white,
          },
        },
      },
      default: {
        default: {
          icon: {
            color: colors.black,
          },
          menu: {
            color: colors.black,
          },
          text: {
            color: colors.black,
          },
        },
        hover: {
          icon: {
            color: colors.white,
          },
          text: {
            color: colors.black,
          },
          headerInfoText: {
            color: colors.black,
          },
        },
        active: {
          menu: {
            color: colors.white,
          },
          text: {
            color: colors.white,
          },
        },
        accessibilityIcon: {
          path1: {
            color: colors.primaryBlue,
          },
          path2: {
            color: colors.primary,
          },
        },
      },
    },
  },
  footer: {
    breakpoint: '72.5rem',
    background: `${colors.almostBlack}`,
    innerWrapper: {
      maxWidth: '90rem',
      paddingInline: `${space.s250}`,
      paddingInlineMobile: '1rem 0',
      paddingMobile: '0',
      padding: '32px 40px 0 40px',
    },
    wrapper: {
      height: {
        mobile: '5.5rem',
        regular: '4rem',
        shrunk: '3rem',
      },
    },
    menus: {
      orderMobile: {
        links: 0,
        copyRight: 2,
        break: 1,
      },
    },
    break: {
      margin: `${space.s200} 0 ${space.s150} 0`,
      marginMobile: `0 0 ${space.s150} 0`,
    },
    tail: {
      height: '0',
      background: colors.primary,
    },
    links: {
      margin: `0`,
      marginMobile: `0 0 ${space.s150} 0`,
      hoverColor: `${colors.primary}`,
    },
    bottomBox: {
      padding: '0 0 20px 0',
      paddingMobile: '0 16px 10px 16px',
      margin: '0',
      marginMobile: `${space.s200} 0 0 0`,
    },
    subscribe: {
      hoverColor: colors.primary,
    },
    BottomItemsWrapper: {
      fontSize: `${fontSizes.p3}`,
    },
  },
  modal: {
    wrapper: { boxShadow: '0 0 1.25rem #0000009a', width: '45rem' },
    header: {
      color: colors.secondary,
      borderWidth: '1px',
    },
    tail: {
      height: '0',
      background: colors.primary,
    },
  },
  navigation: {
    height: '2.5rem',
    list: {
      gap: space.s200,
      paddingRight: space.s150,
    },
    marginLeft: space.s350,
  },
  navigationMenu: {
    gap: space.s150,
    marginRight: space.s150,
  },
  newsLetter: {
    loaderVariant: 'primary',
    button: {
      variant: 'filled',
    },
    successMessage: {
      textAlign: 'center',
      textAlignMobile: 'center',
      margin: space.s300,
      marginMobile: space.s300,
    },
    titleFontWeight: `${fontWeights.bold}`,
    titleFontSize: `${fontSizes.h4}`,
  },
  sectionHeader: {
    borderWidth: 4,
    mobileBorderWidth: 3,
  },
  select: {
    variant: {
      boxed: {
        height: '3rem',
      },
    },
  },
  headerBar: {
    borderWidth: 1,
  },
  input: {
    defaultBorderWidth: 1,
    focusBorderWidth: 1,
    height: {
      m: px2rem(40),
      l: px2rem(48),
    },
    wrapperWidth: px2rem(400),
    variant: {
      boxed: {
        borderWidth: 1,
      },
      boxedBold: {
        borderWidth: 2,
      },
    },
  },
  logo: {
    xs: {
      height: '2.25rem',
      width: '6.5rem',
    },
    sm: {
      height: '2.25rem',
      width: '7.25rem',
    },
    lg: {
      height: '2.75rem',
      width: '8rem',
    },
  },
  parametersPickerContainer: {
    borderBottomWidth: 2,
  },
  radio: {
    borderWidth: 2,
    checkedBorderWidth: 6,
    markColor: colors.primary,
  },
  searchInput: {
    backdropFilter: '3.125rem',
  },
  sideDrawer: {
    width: {
      l: '48rem',
      m: '32rem',
      s: '16rem',
      flexible: '87%',
    },
  },
  buttonSelect: {
    width: px2rem(256),
  },
  tab: {
    bottomUnderlineWidth: 4,
    segmented: {
      activeBackground: colors.primaryBlue,
      activeColor: colors.white,
      background: colors.tertiaryGrey,
      color: colors.secondaryGrey,
    },
  },
  rangeSlider: {
    borderColor: colors.default,
    borderWidth: 2,
    handleColor: colors.white,
    selectedAreaColor: colors.default,
    unselectedAreaColor: colors.white,
  },
  topBanner: {
    height: '45px',
  },
  searchOverlay: {
    searchSection: {
      width: px2rem(800),
    },
    transition: 'color 0.2s linear',
  },
  stateStepper: {
    badge: {
      height: '24px',
      width: '24px',
      borderRadius: '50%',
      borderWidth: '2px',
    },
  },
  stepper: {
    badge: {
      height: '2.5rem',
      width: '2.5rem',
      borderRadius: '50%',
      borderWidth: '4px',
    },
  },
  addToCartDialog: {
    width: '456px',
  },
};
